import React, { useState } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import { Button } from './Button';
import { submitFeedback, type FeedbackFeature, type FeedbackRating } from '../services/feedback';
import { colors, radius, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

interface FeedbackWidgetProps {
  feature: FeedbackFeature;
  // Clé stable du résultat (cf. utils/resultRef) : hashée côté service pour result_ref.
  resultKey: string;
  testID?: string;
}

type Status = 'idle' | 'sending' | 'error';

// Brique discrète de collecte d'avis sur un résultat IA. Non bloquante : l'utilisateur peut
// l'ignorer. Le vote est envoyé dès le tap (upsert) ; le commentaire (positif ou négatif) est optionnel.
export function FeedbackWidget({ feature, resultKey, testID = 'feedback' }: FeedbackWidgetProps) {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [done, setDone] = useState(false); // « Merci » affiché (pouce haut, ou commentaire envoyé)

  const send = async (nextRating: FeedbackRating, withComment: boolean) => {
    setRating(nextRating);
    setStatus('sending');
    try {
      await submitFeedback({
        feature,
        resultKey,
        rating: nextRating,
        comment: withComment ? comment : undefined,
      });
      setStatus('idle');
      // Chaque note ouvre d'abord son champ commentaire ; le « Merci » attend l'envoi du commentaire.
      if (withComment) setDone(true);
    } catch {
      setStatus('error');
    }
  };

  // Tap sur un pouce. Au CHANGEMENT d'avis, on vide le commentaire de l'autre note (sinon un
  // commentaire positif resterait collé à un vote négatif) → l'upsert écrit comment = null.
  const onThumb = (value: FeedbackRating) => {
    if (rating !== null && rating !== value) {
      setComment(''); // pas de commentaire de l'autre note collé au nouveau vote
      setDone(false); // nouvelle note → ré-ouvre son champ commentaire
      void send(value, false);
    } else {
      void send(value, comment.trim().length > 0);
    }
  };

  const thumb = (value: FeedbackRating, icon: 'thumbs-up' | 'thumbs-down') => {
    const active = rating === value;
    return (
      <Pressable
        testID={`${testID}-${value}`}
        accessibilityRole="button"
        accessibilityLabel={value === 'up' ? t('a11y.useful') : t('a11y.notUseful')}
        onPress={() => onThumb(value)}
        style={[styles.thumb, active && styles.thumbActive]}
        hitSlop={8}
      >
        <Ionicons name={icon} size={20} color={active ? colors.white : colors.grey500} />
      </Pressable>
    );
  };

  return (
    <View testID={testID} style={styles.container}>
      <View style={styles.row}>
        <Text style={styles.question}>{t('misc.feedbackQuestion')}</Text>
        <View style={styles.thumbs}>
          {thumb('up', 'thumbs-up')}
          {thumb('down', 'thumbs-down')}
        </View>
      </View>

      {rating !== null && !done ? (
        <View style={styles.commentBlock}>
          <TextInput
            testID={`${testID}-comment`}
            style={styles.input}
            placeholder={
              rating === 'up'
                ? 'Qu’est-ce qui t’a plu ? (facultatif)'
                : 'Qu’est-ce qui n’allait pas ? (facultatif)'
            }
            placeholderTextColor={colors.grey400}
            multiline
            value={comment}
            onChangeText={setComment}
          />
          <Button
            title={t('misc.send')}
            variant="secondary"
            onPress={() => send(rating, true)}
            loading={status === 'sending'}
            testID={`${testID}-send`}
          />
        </View>
      ) : null}

      {done ? (
        <Text testID={`${testID}-thanks`} style={styles.thanks}>
          Merci pour ton retour 🙏
        </Text>
      ) : null}
      {status === 'error' ? (
        <Text testID={`${testID}-error`} style={styles.error}>
          Envoi impossible. Réessaie.
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.grey200,
    backgroundColor: colors.white,
  },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md },
  question: { flex: 1, fontSize: typography.small.size, color: colors.grey700 },
  thumbs: { flexDirection: 'row', gap: spacing.sm },
  thumb: {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.grey50,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  thumbActive: { backgroundColor: colors.electric, borderColor: colors.electric },
  commentBlock: { gap: spacing.sm },
  input: {
    minHeight: 64,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    padding: spacing.md,
    textAlignVertical: 'top',
    fontSize: typography.small.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },
  thanks: { fontSize: typography.small.size, color: colors.success },
  error: { fontSize: typography.small.size, color: colors.error },
});
